import { useCallback, useContext } from 'react'
import { db } from '../firebase/config'
import { SavedIcon } from '../StateManagment/SavedIcon'
import useDebouncedWrite, { SAVE_DELAY_MS } from './useDebouncedWrite'

/**
 * Schrijft losse velden weg zonder opslaanknop, maar niet per toetsaanslag.
 *
 * **Waarom deze hook bestaat.** De notitieschermen schreven bij élke aanslag een
 * aparte `.update()` naar Firestore. Firestore zet schrijfacties naar hetzelfde
 * document achter elkaar in de rij, dus een tekst van 405 tekens werd 405
 * opeenvolgende rondgangen die tientallen seconden nodig hadden om leeg te
 * lopen. Wie eerder wegnavigeerde verloor alles wat er nog in stond, zonder
 * enig signaal. Gemeten in productiecode: bij 0 én bij 3 seconden wachten waren
 * alle 405 tekens weg, pas na 40 seconden bleven ze staan. Zie docs/inbox.md #155.
 *
 * De wacht-en-schrijf-logica zelf staat in `useDebouncedWrite`; hier zit alleen
 * wat Firestore-eigen is: het document bijwerken en de globale melding zetten.
 * Elk veld heeft zijn eigen wachttijd, zodat typen in de tekst een wachtende
 * titelwijziging niet afbreekt.
 *
 * `TaskNotes.jsx` (notities bij een afspraak) doet dit anders en goed: lokale
 * state met een echte Opslaan-knop. Waar een knop past is dat de betere vorm;
 * deze hook is voor de schermen die bewust zonder knop werken.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 * AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
 *
 * **Waarom er ook een `status` uitkomt.** De globale melding onderin verdwijnt na
 * vier seconden en staat ver van het veld. Een tester die er speciaal op lette
 * miste hem twee keer, terwijl haar doel letterlijk "zeker weten dat het bewaard
 * is" was — en deze schermen hebben geen opslaanknop, dus dat is het enige
 * signaal dat er is. `status` is daarom bedoeld om ergens neer te zetten waar de
 * gebruiker toch al kijkt, en hij **blijft staan**: 'saved' verdwijnt niet
 * vanzelf, maar pas als er weer iets te bewaren valt. Zie docs/inbox.md #162.
 *
 * Mislukt de schrijfactie, dan blijft de status op 'saving' staan. Dat is
 * bedoeld: er staat dan inderdaad nog iets klaar dat niet bewaard is, en 'saved'
 * tonen zou een onwaarheid zijn.
 *
 * @param {string} collection - Firestore-collectie waarin geschreven wordt
 * @param {number} [delay] - Wachttijd in ms na de laatste wijziging
 * @returns {{save: (docid: string, fields: object) => void, status: 'idle' | 'saving' | 'saved'}}
 */
const useAutosaveField = (collection, delay = SAVE_DELAY_MS) => {
    const [, setSaved] = useContext(SavedIcon)

    const write = useCallback(
        (_key, { docid, fields }) => db.collection(collection).doc(docid).update(fields).then(() => setSaved('flex')),
        [collection, setSaved],
    )
    const { save: schedule, status } = useDebouncedWrite(write, delay)

    // Sleutel per document én per veld: een wachtende titel mag niet
    // sneuvelen doordat er in de tekst wordt getypt.
    const save = useCallback(
        (docid, fields) => schedule(`${docid}|${Object.keys(fields).sort().join(',')}`, { docid, fields }),
        [schedule],
    )

    return { save, status: status === 'error' ? 'saving' : status }
}

export { SAVE_DELAY_MS }
export default useAutosaveField
